import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import viCommon from '../locales/vi/common.json';
import viHeader from '../locales/vi/header.json';
import viChat from '../locales/vi/chat.json';
import viAdmin from '../locales/vi/admin.json';
import viSettings from '../locales/vi/settings.json';
import viErrors from '../locales/vi/errors.json';
import viAuth from '../locales/vi/auth.json';
import viModels from '../locales/vi/models.json';

import enCommon from '../locales/en/common.json';
import enHeader from '../locales/en/header.json';
import enChat from '../locales/en/chat.json';
import enAdmin from '../locales/en/admin.json';
import enSettings from '../locales/en/settings.json';
import enErrors from '../locales/en/errors.json';
import enAuth from '../locales/en/auth.json';
import enModels from '../locales/en/models.json';

import { FALLBACK_LANGUAGE, LANGUAGE_STORAGE_KEY, SUPPORTED_NAMESPACES } from './languages';

const resources = {
  vi: {
    common: viCommon,
    header: viHeader,
    chat: viChat,
    admin: viAdmin,
    settings: viSettings,
    errors: viErrors,
    auth: viAuth,
    models: viModels
  },
  en: {
    common: enCommon,
    header: enHeader,
    chat: enChat,
    admin: enAdmin,
    settings: enSettings,
    errors: enErrors,
    auth: enAuth,
    models: enModels
  }
};

// Custom language detector configuration
const languageDetectorOptions = {
  // Order and from where user language should be detected
  order: ['localStorage', 'navigator', 'htmlTag'],

  // Keys or params to lookup language from
  lookupQuerystring: 'lng',
  lookupCookie: 'i18next',
  lookupLocalStorage: LANGUAGE_STORAGE_KEY,
  lookupSessionStorage: 'i18nextLng',
  lookupFromPathIndex: 0,
  lookupFromSubdomainIndex: 0,

  // Cache user language
  caches: ['localStorage'],
  excludeCacheFor: ['cimode'], // Languages to not persist

  // Optional expire and domain for set cookie
  cookieMinutes: 10,
  cookieDomain: 'myDomain',

  // Optional htmlTag with lang attribute
  htmlTag: document.documentElement,

  // Optional set htmlTag lang attribute to
  checkWhitelist: true
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: FALLBACK_LANGUAGE,
    defaultNS: 'common',
    ns: SUPPORTED_NAMESPACES,

    detection: languageDetectorOptions,

    interpolation: {
      escapeValue: false // React already escapes by default
    },

    react: {
      useSuspense: false, // Disable suspense mode for SSR compatibility
      bindI18n: 'languageChanged',
      bindI18nStore: '',
      transEmptyNodeValue: '',
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p', 'span'],
      transWrapTextNodes: ''
    },

    // Development options
    debug: false, // Disabled to reduce console logs

    // Loading options
    load: 'languageOnly', // Remove if you want to use locales like en-US

    // Missing key handling
    saveMissing: process.env.NODE_ENV === 'development',
    missingKeyHandler: (_lng, _ns, _key, _fallbackValue) => {
      // Temporarily disabled to reduce console logs
      // if (process.env.NODE_ENV === 'development') {
      //   console.warn(`Missing translation key: ${ns}:${key} for language: ${lng}`);
      // }
    },

    // Parsing options
    parseMissingKeyHandler: (key) => {
      // Temporarily disabled to reduce console logs
      // if (process.env.NODE_ENV === 'development') {
      //   console.warn(`Unparsed key: ${key}`);
      // }
      return key;
    },

    // Backend options (for dynamic loading if needed in the future)
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    // Whitelist languages to prevent XSS
    supportedLngs: ['vi', 'en'],
    nonExplicitSupportedLngs: false,

    // CleanCode options
    cleanCode: true,
    returnNull: false,
    returnEmptyString: true,
    returnObjects: false,
    joinArrays: false,

    // Plurals
    pluralSeparator: '_',
    contextSeparator: '_',

    // Nesting
    nsSeparator: ':',
    keySeparator: '.',

    // Performance
    initImmediate: false
  });

// Hot reload in development
if (process.env.NODE_ENV === 'development' && (module as any).hot) {
  (module as any).hot.accept(['../locales/vi/common.json'], () => {
    // Re-import and update resources
    i18n.addResourceBundle('vi', 'common', viCommon, true, true);
  });

  (module as any).hot.accept(['../locales/en/common.json'], () => {
    i18n.addResourceBundle('en', 'common', enCommon, true, true);
  });
}

export default i18n;